import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Home,
  Briefcase,
  BarChart3,
  BookOpen,
  HelpCircle,
  Settings,
  LogOut,
  Shield,
  Menu,
  X,
  ChevronDown
} from 'lucide-react';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { WalletMultiButton } from '../context/SimpleWalletContext';

// Type definitions
interface NavItem {
  path: string;
  label: string;
  icon: any;
  hasDropdown?: boolean;
  dropdownItems?: DropdownItem[];
}

interface DropdownItem {
  path: string;
  label: string;
}

export default function Header() {
  const { user, logout } = useFirebaseAuth();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isInvestDropdownOpen, setIsInvestDropdownOpen] = useState(false);

  const isActive = (path: string) => location.pathname === path;

  // Main 5-page navigation structure
  const mainNavItems: NavItem[] = [
    { path: '/', label: 'Home', icon: Home },
    {
      path: '/invest',
      label: 'Invest',
      icon: Briefcase,
      hasDropdown: true,
      dropdownItems: [
        { path: '/invest/solar', label: 'Solar' },
        { path: '/invest/wind', label: 'Wind' },
        { path: '/invest/hydro', label: 'Hydro' },
        { path: '/invest/community', label: 'Community Energy' }
      ]
    },
    { path: '/track-impact', label: 'Track Impact', icon: BarChart3 },
    { path: '/learn', label: 'Learn', icon: BookOpen },
    { path: '/support', label: 'Support', icon: HelpCircle },
  ];

  // Auth navigation items (shown when user is logged in)
  const authNavItems: NavItem[] = [
    { path: '/admin', label: 'Admin', icon: Settings },
  ];

  // Show main navigation + auth items for logged-in users
  const allNavItems = user ? [...mainNavItems, ...authNavItems] : mainNavItems;

  return (
    <>
      <motion.header
        className="bg-white shadow-sm border-b border-gray-200 fixed top-0 left-0 right-0 z-50"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-7xl mx-auto pl-0 pr-4 sm:pl-0 sm:pr-6 lg:pl-0 lg:pr-8">
          <div className="flex items-center h-16">
            {/* Logo - Far left with additional left shift */}
            <Link to="/" className="flex items-center flex-shrink-0 -ml-4 sm:-ml-8 lg:-ml-12">
              <motion.div
                className="flex items-center"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <img
                  src="/assets/images/WhatsApp_Image_2025-10-27_at_10.45.25_PM-removebg-preview.png"
                  alt="GREENOVA8 Logo"
                  className="h-40 w-[350px] object-contain"
                />
              </motion.div>
            </Link>

            {/* Desktop Navigation - Perfectly centered */}
            <nav className="hidden md:flex items-center space-x-6 flex-1 justify-center">
              {allNavItems.map((item) => {
                const Icon = item.icon;
                return item.hasDropdown ? (
                  <div key={item.path} className="relative">
                    <div
                      className="flex items-center space-x-1 px-1 py-2 text-sm font-medium transition-all duration-200 cursor-pointer text-gray-600 hover:text-gray-900"
                      onMouseEnter={() => setIsInvestDropdownOpen(true)}
                      onMouseLeave={() => setIsInvestDropdownOpen(false)}
                    >
                      {Icon && <Icon className="w-3 h-3" />}
                      <span>{item.label}</span>
                      <ChevronDown className="w-3 h-3" />

                      {/* Dropdown Menu */}
                      {isInvestDropdownOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                          onMouseEnter={() => setIsInvestDropdownOpen(true)}
                          onMouseLeave={() => setIsInvestDropdownOpen(false)}
                        >
                          {item.dropdownItems?.map((dropdownItem: DropdownItem) => (
                            <Link
                              key={dropdownItem.path}
                              to={dropdownItem.path}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                              {dropdownItem.label}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </div>
                  </div>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center space-x-1 px-1 py-2 text-sm font-medium transition-all duration-200 ${isActive(item.path)
                      ? 'text-gray-900 border-b-2 border-gray-900'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    {Icon && <Icon className="w-3 h-3" />}
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>

            {/* Right Side Items - Far right */}
            <div className="flex items-center space-x-3 flex-shrink-0 ml-auto">
              {user ? (
                <>
                  {/* Wallet Connection */}
                  <div className="hidden lg:block">
                    <WalletMultiButton />
                  </div>

                  {/* Logout Button */}
                  <motion.button
                    onClick={logout}
                    className="bg-gray-100 text-gray-700 hover:bg-gray-200 flex items-center space-x-1 px-2 py-1.5 text-xs font-medium rounded transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <LogOut className="w-3 h-3" />
                    <span className="hidden sm:inline">Logout</span>
                  </motion.button>
                </>
              ) : (
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Link
                    to="/login"
                    className="bg-gray-800 text-white hover:bg-gray-700 flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors"
                  >
                    <Shield className="w-4 h-4" />
                    <span>Login</span>
                  </Link>
                </motion.div>
              )}

              {/* Mobile menu button */}
              {user && (
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="md:hidden p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-gray-600"
                >
                  {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && user && (
          <motion.div
            className="fixed top-16 left-0 right-0 z-40 bg-white shadow-lg border-b border-gray-200"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <div className="px-4 py-6 space-y-4">
              {/* Mobile Navigation Links */}
              {allNavItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center space-x-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${isActive(item.path)
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {Icon && <Icon className="w-5 h-5" />}
                    <span>{item.label}</span>
                  </Link>
                );
              })}

              {/* Mobile Wallet Connection & User Info */}
              <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                <div className="flex items-center space-x-3 px-4 py-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {user.email?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {user.displayName || user.email?.split('@')[0] || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 truncate mt-1">
                      {user.email?.split('@')[0] || 'user'}
                    </p>
                  </div>
                </div>

                <WalletMultiButton />
              </div>
            </div>
          </motion.div>
        )}
      </motion.header>

      {/* Spacer to prevent content from being hidden behind fixed header */}
      <div className="h-16"></div>
    </>
  );
}